import * as React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import { cn } from '../../lib/cn';

export interface StackedBarDatum {
  label: string;
  commit?: number;
  bestCase?: number;
  pipeline?: number;
  /** null = no quota configured (distinct from a quota of zero). */
  quota?: number | null;
}

export interface StackedBarWithReferenceProps {
  data: StackedBarDatum[];
  /** Format the bar value labels and tooltips. */
  formatValue?: (n: number) => string;
  /** Format Y-axis ticks — defaults to compact. */
  formatTick?: (n: number) => string;
  /** Label for the reference line legend. */
  referenceLabel?: string;
  emptyMessage?: string;
  className?: string;
  height?: number;
}

const fmtNum = (n: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

const fmtTick = (n: number) => {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}K`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
};

export function StackedBarWithReference({
  data,
  formatValue = fmtNum,
  formatTick = fmtTick,
  referenceLabel = 'Quota',
  emptyMessage = 'No data',
  className,
  height = 320,
}: StackedBarWithReferenceProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center rounded-lg border border-dashed border-border bg-bg-surface text-text-muted',
          className,
        )}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  const quota = data[0]?.quota ?? 0;

  return (
    <div className={cn('rounded-lg border border-border bg-bg-surface p-4', className)}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart data={data} margin={{ top: 8, right: 16, left: 4, bottom: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
          />
          <YAxis
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            tickFormatter={formatTick}
            tickLine={false}
            axisLine={{ stroke: 'var(--border)' }}
            width={56}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--text-primary)',
            }}
            formatter={(value: number, name: string) => [formatValue(value), name]}
          />
          <Legend
            verticalAlign="top"
            height={28}
            wrapperStyle={{ fontSize: 12, color: 'var(--text-secondary)' }}
            iconSize={10}
          />
          {/* isAnimationActive={false}: see BarChart.tsx — a bar is absent from
              the DOM until its animation frame runs. */}
          <Bar dataKey="commit" name="Commit" stackId="a" fill="var(--accent-500)" radius={[0, 0, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="bestCase" name="Best case" stackId="a" fill="var(--accent-300)" radius={[0, 0, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="pipeline" name="Pipeline" stackId="a" fill="var(--secondary-300)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
          {quota > 0 && (
            <ReferenceLine
              y={quota}
              stroke="var(--text-muted)"
              strokeDasharray="4 4"
              label={{
                value: referenceLabel,
                position: 'right',
                fill: 'var(--text-muted)',
                fontSize: 11,
              }}
            />
          )}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}
